import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatBDT } from '@maktech/shared';
import { api } from '../../api/client';
import { Modal } from '../../components/Modal';
import { useAuth } from '../auth/AuthContext';
import { useCart } from './CartContext';

/**
 * Cart — renderCartModal() (:1282) + the v13 books block (:5248).
 *
 * EVERY NUMBER HERE COMES FROM THE SERVER (/cart/quote). Nothing is summed in
 * the browser. That is not fussiness: the prototype computed totals client-side
 * in cartTotals() (:2255), which is exactly where its bundle discount went
 * missing — the display and the charge were two separate calculations, and one
 * of them was wrong. One source, no drift.
 */

export interface Quote {
  items: { slug: string; title: string; price: number; icon: string | null; instructor: string }[];
  subtotal: number;
  bundles: { slug: string; title: string; discountPct: number; discount: number }[];
  bundleDiscount: number;
  couponCode: string | null;
  couponPct: number;
  couponDiscount: number;
  total: number;
}

export function CartModal({
  onClose,
  onCheckoutCourses,
  onCheckoutBooks,
}: {
  onClose: () => void;
  onCheckoutCourses: (quote: Quote, coupon: string | null) => void;
  onCheckoutBooks: () => void;
}) {
  const { courses, books, removeCourse, setBookQty } = useCart();
  const { user } = useAuth();
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState<string | null>(null);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);

  const { data: quote } = useQuery({
    queryKey: ['quote', courses, coupon],
    queryFn: async () =>
      (await api.post<Quote>('/cart/quote', { courseSlugs: courses, couponCode: coupon ?? undefined })).data,
  });

  const { data: bookData } = useQuery({
    queryKey: ['books'],
    queryFn: async () =>
      (await api.get<{ books: { slug: string; title: string; price: number }[] }>('/books')).data,
    enabled: books.length > 0,
  });

  const bookLines = books
    .map((l) => {
      const b = bookData?.books.find((x) => x.slug === l.slug);
      return b ? { ...l, title: b.title, price: b.price } : null;
    })
    .filter((x): x is { slug: string; qty: number; title: string; price: number } => x !== null);

  const booksSubtotal = bookLines.reduce((s, l) => s + l.price * l.qty, 0);
  const DELIVERY = 6000; // ৳60 — the server charges this; shown for parity

  async function applyCoupon() {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    // applyCoupon() (:2262) — valid applies, anything else clears.
    const r = await api.get<{ valid: boolean; pct?: number }>(`/coupons/${code}`).catch(() => null);
    if (r?.data.valid) {
      setCoupon(code);
      setCouponMsg(`Coupon applied: ${code} (−${r.data.pct}%)`);
    } else {
      setCoupon(null);
      setCouponMsg('Sorry, that coupon code is not valid');
    }
  }

  const empty = courses.length === 0 && books.length === 0;

  return (
    <Modal onClose={onClose} label="Your cart">
      <h3 style={{ marginBottom: 10 }}>Your cart</h3>
      <div id="cartBody">
        {empty ? (
          <div className="empty" data-testid="cart-empty" style={{ padding: '30px 10px' }}>
            <div className="big">🛒</div>
            <b>Your cart is empty.</b>
            <p>Add a premium course and it will appear here.</p>
          </div>
        ) : null}

        {quote?.items.map((c) => (
          <div className="cart-row" key={c.slug} data-testid={`cart-row-${c.slug}`}>
            <div className="cart-thumb">{c.icon}</div>
            <div style={{ flex: 1 }}>
              <b style={{ fontSize: 14 }}>{c.title}</b>
              <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{c.instructor}</div>
            </div>
            <b>{formatBDT(c.price)}</b>
            <button className="btn btn-ghost btn-sm" data-testid={`cart-remove-${c.slug}`} onClick={() => removeCourse(c.slug)}>
              ✕
            </button>
          </div>
        ))}

        {quote && quote.items.length > 0 ? (
          <>
            <div className="sumRow" style={{ marginTop: 14 }}>
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatBDT(quote.subtotal)}</span>
            </div>

            {/* The line the prototype never had — the bundle actually coming off. */}
            {quote.bundles.map((b) => (
              <div className="sumRow" style={{ color: 'var(--teal)' }} key={b.slug} data-testid={`cart-bundle-${b.slug}`}>
                <span>
                  🧩 {b.title} (−{b.discountPct}%)
                </span>
                <span>−{formatBDT(b.discount)}</span>
              </div>
            ))}

            {quote.couponDiscount > 0 ? (
              <div className="sumRow" style={{ color: 'var(--teal)' }} data-testid="cart-coupon-line">
                <span>
                  Coupon {quote.couponCode} (−{quote.couponPct}%)
                </span>
                <span>−{formatBDT(quote.couponDiscount)}</span>
              </div>
            ) : null}

            <div className="sumRow total">
              <span>Total</span>
              <span data-testid="cart-total">{formatBDT(quote.total)}</span>
            </div>

            <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
              <input
                id="couponInput"
                data-testid="coupon-input"
                placeholder="Coupon code (try MAK10)"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', border: '1.5px solid var(--line)', borderRadius: 10, fontFamily: 'inherit' }}
              />
              <button className="btn btn-ghost" data-testid="coupon-apply" onClick={() => void applyCoupon()}>
                Apply
              </button>
            </div>
            {couponMsg ? (
              <p className="asub" data-testid="coupon-msg" style={{ fontSize: 12.5, marginTop: -6 }}>
                {couponMsg}
              </p>
            ) : null}

            <button
              className="btn btn-primary btn-block btn-lg"
              data-testid="cart-checkout"
              onClick={() => (user ? onCheckoutCourses(quote, coupon) : alert('Please sign up or log in first'))}
            >
              Checkout {quote.items.length} course{quote.items.length > 1 ? 's' : ''}
            </button>
          </>
        ) : null}

        {/* v13 (:5250) — books ride in the same cart but check out separately. */}
        {bookLines.length ? (
          <div id="cartBooks" className="cart-books">
            <b style={{ fontSize: 13.5 }}>📚 Books (delivered)</b>
            {bookLines.map((l) => (
              <div className="cb-row" key={l.slug} data-testid={`cart-book-${l.slug}`}>
                <span>{l.title}</span>
                <span className="cb-qty">
                  <button data-testid={`book-minus-${l.slug}`} onClick={() => setBookQty(l.slug, -1)}>
                    −
                  </button>
                  <b data-testid={`book-qty-${l.slug}`}>{l.qty}</b>
                  <button data-testid={`book-plus-${l.slug}`} onClick={() => setBookQty(l.slug, 1)}>
                    +
                  </button>
                </span>
                <span>{formatBDT(l.price * l.qty)}</span>
              </div>
            ))}
            <div className="cb-row" style={{ border: 'none' }}>
              <span className="asub">Delivery</span>
              <span />
              <span className="asub">{formatBDT(DELIVERY)}</span>
            </div>
            <div className="cb-row" style={{ border: 'none', fontWeight: 800 }}>
              <span>Books total</span>
              <span />
              <span data-testid="books-total">{formatBDT(booksSubtotal + DELIVERY)}</span>
            </div>
            <button
              className="btn btn-ghost btn-sm btn-block"
              data-testid="books-checkout"
              onClick={() => (user ? onCheckoutBooks() : alert('Please sign up or log in first'))}
            >
              Checkout books (cash on delivery)
            </button>
            <p className="asub" style={{ fontSize: 11.5, marginTop: 6 }}>
              Courses and books check out separately — books need a delivery address.
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
